import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useMarkAccountAsReviewedMutation } from "store/apis/bankConnections";
import { BankConnection } from "types/Models/banks";
import { BackendError } from "types/utils/error";
import { formatDate } from "utils/formatDate";

export const ReviewNewBankAccounts = ({
  onManage,
  entityItemId,
  accounts,
  close,
}: {
  accounts: BankConnection["bank_accounts"];
  entityItemId: string;
  close: () => void;
  onManage: () => void;
}) => {
  const entityId = useCurrentEntityId();
  const groupId = useCurrentGroupContext().uuid;

  const [markAccountAsReviewed, { isLoading }] =
    useMarkAccountAsReviewedMutation();
  const { successToast, alertToast } = useToast();

  const reviewAccounts = async () => {
    try {
      await markAccountAsReviewed({
        entityId,
        entityItemId: entityItemId,
        groupId,
      }).unwrap();
      successToast({
        message: "Accounts reviewed successfully",
      });
      close();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  return (
    <>
      <Modal.Header>
        <Modal.Title>View new accounts</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="t-mb-5 t-text-body-sm t-text-text-60">
          View your newly syncing bank accounts here. To make any changes, click
          on 'Manage Accounts' and update the selection.
        </div>
        <div className="t-flex t-flex-col t-gap-4">
          {accounts.map((account) => (
            <div
              className="t-border-solid t-border-neutral-0 t-rounded-lg t-px-2 t-py-4 t-flex t-gap-1 t-flex-col t-border"
              key={account.uuid}
            >
              <span className="t-text-subtext">
                {account.name} •••• {account.account_number.substring(0, 4)}
              </span>

              <span className="t-text-text-30 t-text-body-sm">
                Account balance:{" "}
                <AmountSuperScript amount={Number(account.available_balance)} />
              </span>
              <span className="t-text-text-30 t-text-body-sm">
                Opening balance date: {formatDate(account.opening_balance_date)}
              </span>
            </div>
          ))}
        </div>
      </Modal.Body>
      <Modal.FooterButtonGroup>
        <Button onClick={onManage}>Manage accounts</Button>
        <Button
          customType="primary"
          onClick={reviewAccounts}
          isLoading={isLoading}
        >
          Ok, got it
        </Button>
      </Modal.FooterButtonGroup>
    </>
  );
};
